/**
 * Rolling in-memory conversation history for the active page session.
 * Mirrors the shape used by the native app so the system prompt is
 * identical across surfaces. Intentionally *not* persisted — each page
 * load starts fresh, as called out in the plan's out-of-scope list.
 */

import type { AnthropicContentBlock, ConversationMessage } from "../types";

const MAX_TURNS_RETAINED = 20;

export class ConversationHistory {
  private messages: ConversationMessage[] = [];

  appendUserMessage(content: string): void {
    this.messages.push({ role: "user", content });
    this.trim();
  }

  appendAssistantMessage(content: string): void {
    this.messages.push({ role: "assistant", content });
    this.trim();
  }

  /**
   * Variant for assistant turns that included `tool_use` blocks.
   * Stored as the structured Anthropic content array so the next
   * outgoing user message can synthesize matching `tool_result`
   * blocks (Anthropic 400s without them).
   */
  appendAssistantMessageWithToolUses(
    text: string,
    toolUseBlocks: readonly { id: string; name: string; input: unknown }[],
  ): void {
    const content: AnthropicContentBlock[] = [];
    if (text.length > 0) {
      content.push({ type: "text", text });
    }
    for (const block of toolUseBlocks) {
      content.push({
        type: "tool_use",
        id: block.id,
        name: block.name,
        input: block.input,
      });
    }
    this.messages.push({ role: "assistant", content });
    this.trim();
  }

  snapshot(): readonly ConversationMessage[] {
    return this.messages;
  }

  reset(): void {
    this.messages = [];
  }

  private trim(): void {
    if (this.messages.length > MAX_TURNS_RETAINED) {
      this.messages = this.messages.slice(-MAX_TURNS_RETAINED);
    }
  }
}
